import {Helmet} from '@dr.pogodin/react-helmet';
import {DataStatus, Form, FormSubmitHandler} from 'platform/components';
import {DeepPartial} from 'utility-types';

import {useDispatch} from 'react-redux';
import {useLocation, useParams} from 'react-router-dom';

import {defaultTo, isNil, mergeAll} from 'ramda';
import {isBoolean, isNilOrEmpty} from 'ramda-adjunct';

import {documentContextApi} from '@dms/api/core';
import {VatRateType} from '@dms/api/shared';
import {
  useGetServiceOrderTypesQuery,
  useGetServiceOrderVariantAccountingQuery,
  useGetServiceOrderVariantDefaultWarehousePriorityQuery,
  useGetServiceOrderVariantDocumentsQuery,
  useGetServiceOrderVariantIntegrationQuery,
  useGetServiceOrderVariantMandatoryFieldsQuery,
  useGetServiceOrderVariantPaymentTypeQuery,
  useGetServiceOrderVariantQuery,
  useGetServiceOrderVariantReportingQuery,
  useGetServiceOrderVariantWarehousePriorityQuery,
  usePatchServiceOrderVariantAccountingMutation,
  usePatchServiceOrderVariantDocumentsMutation,
  usePatchServiceOrderVariantIntegrationMutation,
  usePatchServiceOrderVariantMandatoryFieldsMutation,
  usePatchServiceOrderVariantMutation,
  usePatchServiceOrderVariantPaymentTypeMutation,
  usePatchServiceOrderVariantReportingMutation,
  usePatchServiceOrderVariantWarehousePriorityMutation,
  usePostServiceOrderVariantMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {
  decimalValueToPercentage,
  percentageToDecimalValue,
  SettingsTemplate,
} from '@dms/settings/shared';
import {handleApiError, queryParams} from '@dms/shared';

import {stringToBoolean, useNavigate, useQueryState} from 'shared/web';

import {AccountingTab} from './(sections)/AccountingTab/AccountingTab';
import {DocumentsTab} from './(sections)/DocumentsTab/DocumentsTab';
import {GeneralTab} from './(sections)/GeneralTab/GeneralTab';
import {IntegrationTab} from './(sections)/IntegrationTab/IntegrationTab';
import {MandatoryFieldsTab} from './(sections)/MandatoryFieldsTab/MandatoryFieldsTab';
import {PaymentTab} from './(sections)/PaymentTab/PaymentTab';
import {ReportingTab} from './(sections)/ReportingTab/ReportingTab';
import {WarehousePrioritiesTab} from './(sections)/WarehousePrioritiesTab/WarehousePrioritiesTab';
import {mandatoryConditions} from './conditions/mandatoryConditions';
import {VEHICLE_MANDATORY_FIELDS} from './constants/vehicleMandatoryFields';
import {FormType, MandatoryFieldType} from './types/ServiceOrderVariant';
import {ServiceOrderVariantsDetailFormSchema} from './types/ServiceOrderVariantsDetailFormSchema';
import {getFieldsByOrderType} from './utils/getFieldsByOrderType';
import {getServiceOrderVariantsRoutes} from './utils/getServiceOrderVariantsRoutes';

const GENERAL_TAB = 'general';
const DOCUMENTS_TAB = 'documents';
const PAYMENTS_TAB = 'payments';
const MANDATORY_FIELDS_TAB = 'mandatoryFields';
const INTEGRATION_TAB = 'integration';
const ACCOUNTING_TAB = 'accounting';
const REPORTING_TAB = 'reporting';
const WAREHOUSES_TAB = 'warehouses';

const DEFAULT_VAT_RATE: VatRateType = 'S';

export function ServiceOrderVariantsDetail() {
  const {id} = useParams();
  const {pathname} = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [, updateQuery] = useQueryState(queryParams.COMPONENT_SECTIONS_TAB);
  const serviceOrderVariantsRoutes = getServiceOrderVariantsRoutes(pathname);

  const isSalesRoute = pathname.startsWith(settingsRoutes.salesServiceOrderVariants);

  const {
    data: orderVariant,
    isLoading: isOrderVariantLoading,
    isError: isOrderVariantError,
  } = useGetServiceOrderVariantQuery(
    {serviceOrderVariantId: id as string},
    {skip: isNilOrEmpty(id)}
  );
  const {
    data: orderVariantDocuments,
    isLoading: isOrderVariantDocumentsLoading,
    isError: isOrderVariantDocumentsError,
  } = useGetServiceOrderVariantDocumentsQuery(
    {serviceOrderVariantId: id as string},
    {skip: isNilOrEmpty(id)}
  );
  const {
    data: mandatoryFields,
    isLoading: isMandatoryFieldsLoading,
    isError: isMandatoryFieldsError,
  } = useGetServiceOrderVariantMandatoryFieldsQuery(
    {serviceOrderVariantId: id as string},
    {skip: isNilOrEmpty(id)}
  );
  const {
    data: orderVariantPaymentType,
    isLoading: isOrderVariantPaymentTypeLoading,
    isError: isOrderVariantPaymentTypeError,
  } = useGetServiceOrderVariantPaymentTypeQuery(
    {serviceOrderVariantId: id as string},
    {skip: isNilOrEmpty(id)}
  );
  const {
    data: orderVariantAccounting,
    isFetching: isOrderVariantAccountingFetching,
    isError: isOrderVariantAccountingError,
  } = useGetServiceOrderVariantAccountingQuery(
    {serviceOrderVariantId: id as string},
    {skip: isNilOrEmpty(id)}
  );
  const {
    data: orderVariantReporting,
    isFetching: isOrderVariantReportingFetching,
    isError: isOrderVariantReportingError,
  } = useGetServiceOrderVariantReportingQuery(
    {serviceOrderVariantId: id as string},
    {skip: isNilOrEmpty(id)}
  );
  const {
    data: orderVariantIntegration,
    isFetching: isOrderVariantIntegrationFetching,
    isError: isOrderVariantIntegrationError,
  } = useGetServiceOrderVariantIntegrationQuery(
    {serviceOrderVariantId: id as string},
    {skip: isNilOrEmpty(id)}
  );
  const {
    data: defaultWarehousePriority,
    isFetching: isDefaultWarehousePriorityFetching,
    isError: isDefaultWarehousePriorityError,
  } = useGetServiceOrderVariantDefaultWarehousePriorityQuery({}, {skip: !isNilOrEmpty(id)});
  const {
    data: warehousePriorities,
    isLoading: isWarehousePrioritiesLoading,
    isError: isWarehousePrioritiesError,
  } = useGetServiceOrderVariantWarehousePriorityQuery(
    {serviceOrderVariantId: id as string},
    {skip: isNilOrEmpty(id)}
  );
  const {data: orderTypes} = useGetServiceOrderTypesQuery();

  const [postOrderVariant] = usePostServiceOrderVariantMutation();
  const [patchOrderVariant] = usePatchServiceOrderVariantMutation();
  const [patchOrderVariantDocuments] = usePatchServiceOrderVariantDocumentsMutation();
  const [patchServiceOrderVariantMandatoryFields] =
    usePatchServiceOrderVariantMandatoryFieldsMutation();
  const [patchServiceOrderVariantAccounting] = usePatchServiceOrderVariantAccountingMutation();
  const [patchServiceOrderVariantReporting] = usePatchServiceOrderVariantReportingMutation();
  const [patchPaymentType] = usePatchServiceOrderVariantPaymentTypeMutation();
  const [patchServiceOrderVariantIntegration] = usePatchServiceOrderVariantIntegrationMutation();
  const [patchServiceOrderVariantWarehousePriority] =
    usePatchServiceOrderVariantWarehousePriorityMutation();

  const handleSubmit: FormSubmitHandler<FormType> = async (data) => {
    const body = {
      ...data.general,
      isActive: orderVariant?.isActive ?? true,
      serviceCaseTypeId: data.general?.serviceCaseTypeId ?? '',
      isDefaultForServiceCaseType: !!data.general?.isDefaultForServiceCaseType,
      serviceOrderTypeId: data.general?.serviceOrderTypeId ?? '',
      name: data.general?.name ?? '',
      priceType: data.general?.priceType ?? '',
      priceTypeRatio: percentageToDecimalValue(data.general?.priceTypeRatio ?? 100),
      rateType: data.general?.rateType ?? '',
      rateTypeRatio: percentageToDecimalValue(data.general?.rateTypeRatio ?? 100),
      minimalMaterialMargin: percentageToDecimalValue(data.general?.minimalMaterialMargin ?? 0),
      minimalWorkMargin: percentageToDecimalValue(data.general?.minimalWorkMargin ?? 0),
      suffix: data.general?.suffix ?? '',
      isUnitPriceWithVat: data.general?.isUnitPriceWithVat ?? false,
      isChangeableDefaultCustomer: data.general?.isChangeableDefaultCustomer ?? false,
      isCancelReasonRequired: data.general?.isCancelReasonRequired ?? false,
    };

    const orderCode = orderTypes?.find((type) => type?.id === body.serviceOrderTypeId)?.code;

    const mandatoryFields = [
      ...getFieldsByOrderType(orderCode),
      ...VEHICLE_MANDATORY_FIELDS.map((field) => field.id),
    ].map((mandatoryField) => ({
      field: mandatoryField,
      isSelected: !!data.mandatoryFields?.[mandatoryField],
      mandatoryCondition:
        mandatoryField === 'generatingOrderSheet'
          ? undefined
          : mandatoryConditions.map((condition) => ({
              event: condition,
              isSelected:
                !!data.mandatoryFields?.[mandatoryField] &&
                !!data.mandatoryFieldConditions?.[mandatoryField]?.includes(condition),
            })),
    }));

    await (
      isNil(id) ? postOrderVariant({body}) : patchOrderVariant({serviceOrderVariantId: id, body})
    )
      .unwrap()
      .then((response) => {
        const tabsArray = [];
        const serviceOrderVariantId = response?.serviceOrderVariantId ?? id ?? '';

        if (data.documents) {
          tabsArray.push(
            patchOrderVariantDocuments({
              serviceOrderVariantId,
              body: data.documents,
            })
              .unwrap()
              .catch(handleApiError)
          );
        }

        if (data.paymentType) {
          tabsArray.push(
            patchPaymentType({
              body: {
                defaultPaymentType: data.paymentType.defaultPaymentType,
                cardPayment: data.paymentType.cardPayment?.isAllowed
                  ? data.paymentType.cardPayment
                  : {isAllowed: false},
                cashPayment: data.paymentType.cashPayment?.isAllowed
                  ? data.paymentType.cashPayment
                  : {isAllowed: false},
                bankPayment: data.paymentType.bankPayment?.isAllowed
                  ? data.paymentType.bankPayment
                  : {isAllowed: false},
                internalPayment: data.paymentType.internalPayment?.isAllowed
                  ? data.paymentType.internalPayment
                  : {isAllowed: false},
              },
              serviceOrderVariantId,
            })
              .unwrap()
              .catch(handleApiError)
          );
        }

        tabsArray.push(
          patchServiceOrderVariantMandatoryFields({
            serviceOrderVariantId,
            body: mandatoryFields,
          })
            .unwrap()
            .catch(handleApiError)
        );

        tabsArray.push(
          patchServiceOrderVariantAccounting({
            serviceOrderVariantId,
            body: {
              ...data.accounting,
              isTaxDeductible: stringToBoolean(data.accounting.isTaxDeductible),
              fixedVatType: stringToBoolean(data.accounting.isTaxDeductible)
                ? undefined
                : data.accounting.fixedVatType,
            },
          })
            .unwrap()
            .catch(handleApiError)
        );

        tabsArray.push(
          patchServiceOrderVariantReporting({
            serviceOrderVariantId,
            body: {
              ...data.reporting,
              isDefaultServiceOrderGroupLocked: !!data.reporting.isDefaultServiceOrderGroupLocked,
              isPreDeliveryInspection: !!data.reporting.isPreDeliveryInspection,
            },
          })
            .unwrap()
            .catch(handleApiError)
        );

        tabsArray.push(
          patchServiceOrderVariantIntegration({
            serviceOrderVariantId,
            body: {
              isDefaultForXentryServiceMeasures:
                !!data.integration?.isDefaultForXentryServiceMeasures,
              isAutomaticSendToChronos: !!data.integration?.isAutomaticSendToChronos,
            },
          })
            .unwrap()
            .catch(handleApiError)
        );

        if (data.warehousePriorities) {
          tabsArray.push(
            patchServiceOrderVariantWarehousePriority({
              serviceOrderVariantId: response?.serviceOrderVariantId ?? id ?? '',
              body: {
                warehouseIds: data.warehousePriorities,
              },
            })
              .unwrap()
              .catch(handleApiError)
          );
        }

        Promise.all(tabsArray)
          .then(() => navigate(serviceOrderVariantsRoutes.overview))
          .then(() =>
            dispatch(
              documentContextApi.util.invalidateTags([{type: 'documentsCount', id: id ?? ''}])
            )
          );
      })
      .catch(handleApiError);
  };

  const mandatoryFieldsArray =
    mandatoryFields?.map(
      (field) =>
        ({
          [field?.field ?? '']: !!field?.isSelected,
        }) as Record<MandatoryFieldType, boolean>
    ) ?? ([] as Record<MandatoryFieldType, boolean>[]);

  const mandatoryFieldConditionsArray =
    mandatoryFields?.map((field) => {
      const selectedEvent = field?.mandatoryCondition?.find(
        (condition) => condition?.isSelected
      )?.event;

      return {
        [field?.field ?? '']: selectedEvent ? [selectedEvent] : [],
      } as Record<MandatoryFieldType, string[]>;
    }) ?? ([] as Record<MandatoryFieldType, string[]>[]);

  const mandatoryFieldsDefaultValues =
    mergeAll<Record<MandatoryFieldType, boolean>>(mandatoryFieldsArray);

  const mandatoryFieldConditionDefaultValues = mergeAll<Record<MandatoryFieldType, string[]>>(
    mandatoryFieldConditionsArray
  );

  const titleKey = isNil(id)
    ? 'entity.order.actions.newServiceOrderVariant'
    : 'entity.order.labels.serviceOrderVariants';

  const warehousePrioritiesSourceData = isNilOrEmpty(id)
    ? defaultWarehousePriority
    : warehousePriorities;

  const defaultValues: DeepPartial<FormType> = {
    general: {
      ...orderVariant,
      suffix: orderVariant?.suffix ?? undefined,
      priceTypeRatio: decimalValueToPercentage(defaultTo(1, orderVariant?.priceTypeRatio)),
      rateTypeRatio: decimalValueToPercentage(defaultTo(1, orderVariant?.rateTypeRatio)),
      minimalMaterialMargin: decimalValueToPercentage(
        defaultTo(0, orderVariant?.minimalMaterialMargin)
      ),
      minimalWorkMargin: decimalValueToPercentage(defaultTo(0, orderVariant?.minimalWorkMargin)),
      isCancelReasonRequired: orderVariant?.isCancelReasonRequired ?? false,
    },
    documents: {
      ...orderVariantDocuments,
      serviceOrderIssue: orderVariantDocuments?.serviceOrderIssue
        ? {
            ...orderVariantDocuments.serviceOrderIssue,
            docSeriesId: orderVariantDocuments.serviceOrderIssue.docSeriesId ?? undefined,
          }
        : undefined,
      serviceOrderReturn: orderVariantDocuments?.serviceOrderReturn
        ? {
            ...orderVariantDocuments.serviceOrderReturn,
            docSeriesId: orderVariantDocuments.serviceOrderReturn.docSeriesId ?? undefined,
          }
        : undefined,
      fastTrackIssue: {
        ...orderVariantDocuments?.fastTrackIssue,
        isEnable: orderVariantDocuments?.fastTrackIssue?.isEnable ?? false,
      },
    },
    paymentType: {
      defaultPaymentType: orderVariantPaymentType?.defaultPaymentType ?? undefined,
      cashPayment: orderVariantPaymentType?.cashPayment ?? {isAllowed: false},
      cardPayment: orderVariantPaymentType?.cardPayment ?? {isAllowed: false},
      bankPayment: orderVariantPaymentType?.bankPayment ?? {isAllowed: false},
      internalPayment: orderVariantPaymentType?.internalPayment ?? {isAllowed: false},
    },
    mandatoryFields: mandatoryFieldsDefaultValues,
    mandatoryFieldConditions: mandatoryFieldConditionDefaultValues,
    reporting: {
      ...orderVariantReporting,
      excludedServiceOrderGroupIds: orderVariantReporting?.excludedServiceOrderGroupIds ?? [],
      isDefaultServiceOrderGroupLocked:
        orderVariantReporting?.isDefaultServiceOrderGroupLocked ?? false,
      isPreDeliveryInspection: orderVariantReporting?.isPreDeliveryInspection ?? false,
    },
    accounting: {
      fixedVatType: (orderVariantAccounting?.fixedVatType as VatRateType) ?? DEFAULT_VAT_RATE,
      excludedServiceCenterIds: orderVariantAccounting?.excludedServiceCenterIds ?? [],
      defaultServiceCenterId: orderVariantAccounting?.defaultServiceCenterId ?? undefined,
      isDefaultServiceCenterLocked: orderVariantAccounting?.isDefaultServiceCenterLocked ?? false,
      isTaxDeductible: isBoolean(orderVariantAccounting?.isTaxDeductible)
        ? (orderVariantAccounting.isTaxDeductible as boolean).toString()
        : 'true',
    },
    integration: {
      isDefaultForXentryServiceMeasures:
        orderVariantIntegration?.isDefaultForXentryServiceMeasures ?? false,
      isAutomaticSendToChronos: orderVariantIntegration?.isAutomaticSendToChronos ?? false,
    },
    warehousePriorities:
      warehousePrioritiesSourceData?.map((warehouse) => warehouse.warehouseId) ?? null,
  };

  return (
    <>
      <Helmet title={i18n.t(titleKey)} />
      <DataStatus
        isLoading={
          isOrderVariantLoading ||
          isOrderVariantDocumentsLoading ||
          isMandatoryFieldsLoading ||
          isOrderVariantPaymentTypeLoading ||
          isOrderVariantAccountingFetching ||
          isOrderVariantReportingFetching ||
          isOrderVariantIntegrationFetching ||
          isDefaultWarehousePriorityFetching ||
          isWarehousePrioritiesLoading
        }
        isError={
          isOrderVariantError ||
          isOrderVariantDocumentsError ||
          isMandatoryFieldsError ||
          isOrderVariantPaymentTypeError ||
          isOrderVariantAccountingError ||
          isOrderVariantReportingError ||
          isOrderVariantIntegrationError ||
          isDefaultWarehousePriorityError ||
          isWarehousePrioritiesError
        }
        minHeight={60}
      >
        <Form<FormType>
          mode="onChange"
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          experimentalZodSchema={ServiceOrderVariantsDetailFormSchema}
          onInvalidSubmit={(error) => {
            if (error.paymentType) {
              return updateQuery(PAYMENTS_TAB);
            }

            if (error.general) {
              return updateQuery(GENERAL_TAB);
            }

            if (error.documents) {
              return updateQuery(DOCUMENTS_TAB);
            }
          }}
          isFullHeight
          isDisabled={isSalesRoute}
        >
          {(control, formApi) => {
            const selectedOrderTypeId = formApi.watch('general.serviceOrderTypeId');
            const selectedOrderType = orderTypes?.find((type) => type?.id === selectedOrderTypeId);

            return (
              <SettingsTemplate
                header={{
                  title: `${
                    orderVariant?.name ?? i18n.t('entity.order.actions.newServiceOrderVariant')
                  }${selectedOrderType ? ` (${selectedOrderType?.name})` : ''}`,
                  breadcrumbs: [
                    {
                      label: i18n.t('entity.order.labels.serviceOrderVariants'),
                      href: serviceOrderVariantsRoutes.overview,
                    },
                  ],
                }}
                tabs={[
                  {
                    title: i18n.t('general.labels.general'),
                    queryId: GENERAL_TAB,
                    content: <GeneralTab control={control} formApi={formApi} />,
                  },
                  {
                    title: i18n.t('general.labels.documents'),
                    queryId: DOCUMENTS_TAB,
                    content: (
                      <DocumentsTab
                        control={control}
                        formApi={formApi}
                        serviceOrderTypeCode={selectedOrderType?.code}
                        serviceOrderVariantId={id}
                      />
                    ),
                  },
                  {
                    title: i18n.t('entity.bank.labels.paymentType'),
                    queryId: PAYMENTS_TAB,
                    content: <PaymentTab control={control} formApi={formApi} />,
                  },
                  {
                    title: i18n.t('entity.accounting.labels.title'),
                    queryId: ACCOUNTING_TAB,
                    content: (
                      <AccountingTab
                        control={control}
                        formApi={formApi}
                        data-testid={testIds.settings.serviceOrderVariantsDetail('accounting')}
                      />
                    ),
                  },
                  {
                    title: i18n.t('entity.order.labels.reporting'),
                    queryId: REPORTING_TAB,
                    content: (
                      <ReportingTab
                        control={control}
                        formApi={formApi}
                        data-testid={testIds.settings.serviceOrderVariantsDetail('reporting')}
                      />
                    ),
                  },
                  {
                    title: i18n.t('entity.order.labels.mandatoryFields'),
                    queryId: MANDATORY_FIELDS_TAB,
                    content: (
                      <MandatoryFieldsTab
                        control={control}
                        formApi={formApi}
                        selectedOrderTypeCode={selectedOrderType?.code}
                      />
                    ),
                    isDisabled: isNilOrEmpty(formApi.watch('general.serviceOrderTypeId')),
                  },
                  {
                    title: i18n.t('entity.warehouse.labels.warehouses'),
                    queryId: WAREHOUSES_TAB,
                    content: (
                      <WarehousePrioritiesTab
                        control={control}
                        formApi={formApi}
                        sourceData={warehousePrioritiesSourceData}
                      />
                    ),
                  },
                  {
                    title: i18n.t('entity.serviceOrder.labels.integration'),
                    queryId: INTEGRATION_TAB,
                    content: <IntegrationTab control={control} formApi={formApi} />,
                    isDisabled: isNilOrEmpty(id),
                  },
                ]}
                isCreating={isNil(id)}
                data-testid={testIds.settings.serviceOrderVariantsDetail('template')}
              />
            );
          }}
        </Form>
      </DataStatus>
    </>
  );
}
